import React, { useState, useEffect, useCallback } from 'react'
import DashboardLayout from '../../../layouts/Dashboard'
import { Col, Row, Badge, Spinner, Form, Modal, Button } from 'react-bootstrap'
import { Warning, Funnel, X } from 'phosphor-react'
import { toast } from 'react-toastify'
import api from '../../../services/api'

import { formatDateTSX } from '../../../utils/tsxFormat'
import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import RequestModal from '../../../components/RequestModal'
import SearchInput from '../../../components/SearchInput'

interface IRequest {
  id: number
  invoice: string
  request_number: string
  invoice_number: string
  receiver_name: string
  status: string
  cep: string
  city: string
  state: string
  neighborhood: string
  street: string
  street_number: string
  complement: string
  start_date: Date
  weight: number
  updated_at: string
  delivering_image_url: string
  delivering_image_key: string
  customer_rating_stars: number
  customer_rating_text: string
  request_delivery_driver_note: string
  request_delivery_time_seconds: number
  request_delivery_distance_meters: number
  vehicle_id: number
  vehicle_board: string
  driver_id: number
  driver_name: string
  delivery_had_problems: boolean
  note: string
  delivery_problem_solution_note: string
  is_internal_transfer: boolean
}

const status = {
  unallocated: { name: 'Não alocado', color: '#cca300' },
  allocated: { name: 'Alocado', color: '#00BFFF' },
  in_progress: { name: 'Em andamento', color: '#120a8f' },
  on_route: { name: 'Em rota', color: '#053C5E' },
  delivered: { name: 'Entregue', color: '#089c08' },
  canceled: { name: 'Cancelado', color: '#ff0000' },
}

const Entregas: React.FC = () => {
  const [requests, setRequests] = useState<IRequest[]>()
  const { isLoading, setIsLoading } = useLoading()
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const [search, setSearch] = useState('')
  const [lastSearch, setLastSearch] = useState('')
  const [filteredStatus, setFilteredStatus] = useState('')

  const [requestsChanged, setRequestsChanged] = useState<boolean>(false)
  const [showRequestModal, setShowRequestModal] = useState<boolean>(false)
  const [requestModal, setRequestModal] = useState<IRequest>()
  const [filterModal, setFilterModal] = useState(false)

  const deliveryStatusMap: any = {
    allocated: 'Alocada',
    on_route: 'Em rota',
    delivered: 'Entregue',
    canceled: 'Cancelada',
  }

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setFilteredStatus('')
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl =
      '/delivery-manager/requests/delivery?status=allocated,on_route,delivered,canceled&limit=20'
    if (search) apiUrl += '&search=' + search

    const offset = requests && !isSearchDifferent ? requests.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      if (!data.length) {
        if (isSearchDifferent) setRequests(data)
        setHasMoreData(false)
      } else {
        if (!requests) setRequests(data)
        else setRequests(isSearchDifferent ? data : requests.concat(data))
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, requests, hasMoreData])

  //useEffect do loading inicial dos dados
  useEffect(() => {
    if (!requests && hasMoreData) loadData()
  }, [loadData, hasMoreData, requests])

  useEffect(() => {
    const handleScroll = () => {
      if (
        Math.abs(
          window.innerHeight +
            document.documentElement.scrollTop -
            document.documentElement.offsetHeight,
        ) > 10 ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  useEffect(() => {
    const interval = setInterval(async () => {
      let apiUrl = `/delivery-manager/requests/delivery?status=allocated,on_route,delivered,canceled&limit=${requests?.length}`

      if (search) apiUrl += '&search=' + search

      const { data } = await api.get(apiUrl)
      if (JSON.stringify(data) !== JSON.stringify(requests)) {
        setRequestsChanged(true)
        setRequests(data)
      }
    }, 10000)

    return () => clearInterval(interval)
  }, [requests, search])

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Entregas">
      <LoadingSpinner text="Buscando entregas. Aguarde..." />
      <Form style={{ color: '#A59E9E' }}>
        <Row style={{ marginTop: '10px', marginInline: 30 }}>
          <SearchInput
            placeholder="Digite o código fiscal, placa do veículo, nome do motorista, nome do cliente ou nº do pedido/nota"
            searchValue={search}
            setValue={setSearch}
            handleSearch={loadData}
            showOptions={false}
          />
          <Col
            className="d-flex justify-content-md-end justify-content-center align-items-center mt-md-0 mt-3"
            md={12}
            lg={4}
          >
            <Button className={styles.filterBtn} onClick={() => setFilterModal(true)}>
              <span style={{ fontSize: 14 }}>Filtrar</span> <Funnel size={20} />
            </Button>
          </Col>
        </Row>
      </Form>
      {requestsChanged && (
        <Row>
          <Badge
            bg=""
            className={
              styles.infoBadge +
              ' d-flex flex-md-row flex-column justify-content-center align-items-center'
            }
          >
            <p className="my-0">A página foi atualizada com novas alterações. Confira-as abaixo.</p>
            <span className="m-2 my-0">
              <p className="m-0">
                <Button
                  color="white"
                  className={styles.closeNotificationButton + ' py-0 px-1 mt-md-0 mt-2'}
                  onClick={() => setRequestsChanged(false)}
                >
                  Fechar
                  <X size={15} weight="bold" style={{ paddingBottom: '2px' }} />
                </Button>
              </p>
            </span>
          </Badge>
        </Row>
      )}
      <Row className={`${styles.cardsContainer} justify-content-center justify-content-md-start`}>
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!requests?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhuma
            entrega de pedido encontrada!
          </Badge>
        )}
        {requests
          ?.filter((r) => r.status === filteredStatus || !filteredStatus)
          .map((request, index) => (
            <Col key={request.id} className={styles.cardCol} md={5} lg={4} xl={2}>
              <div>
                <span className={styles.cardInvoice}>
                  {request.invoice_number ?? request.invoice}
                </span>
                {request.request_number && (
                  <span className={styles.cardRequestNumber}>Pedido {request.request_number}</span>
                )}

                <span className={styles.startDate}>
                  {formatDateTSX(request.start_date, 'dd/MM/yyyy', false)}
                </span>
                <span className={styles.infoSpan}>
                  <strong>Veículo:</strong> {request.vehicle_board}
                </span>
                <span className={styles.infoSpan}>
                  <strong>Peso da entrega:</strong> {request.weight} KG
                </span>
                <span className={styles.infoSpan}>
                  <b>Status: </b>
                  {request?.status && (
                    <span>
                      <Badge
                        bg="unset"
                        color="white"
                        pill
                        className={styles.statusBadge}
                        style={{
                          backgroundColor: status[request.status as keyof typeof status].color,
                        }}
                      >
                        {status[request.status as keyof typeof status].name}
                      </Badge>
                    </span>
                  )}
                </span>
              </div>
              <div>
                <Button
                  className={styles.detailsButton}
                  onClick={() => [setRequestModal(request), setShowRequestModal(true)]}
                >
                  Ver detalhes do pedido
                </Button>
              </div>
            </Col>
          ))}
      </Row>
      {requestModal && (
        <RequestModal
          request={requestModal}
          userRole="manager"
          showEditButton={true}
          showRequestModal={showRequestModal}
          setShowRequestModal={setShowRequestModal}
          onRequestChange={(updatedRequest: IRequest) => setRequestModal(updatedRequest)}
          onRequestDeleted={(deletedRequestId) => {
            setRequests((prevRequests = []) =>
              prevRequests.filter((req) => req.id !== deletedRequestId),
            )
            setShowRequestModal(false)
          }}
        ></RequestModal>
      )}
      <Modal centered show={filterModal} onHide={() => setFilterModal(false)}>
        <Modal.Header closeButton />
        <Modal.Body>
          <span style={{ fontSize: 14, fontWeight: '500' }} className="text-left text-dark">
            Filtrar por status
          </span>
          <Row>
            <Col>
              <Form.Select
                className={styles.statusSelect}
                onChange={(event) => {
                  setFilteredStatus(event.target.value)
                  setFilterModal(false)
                }}
              >
                <option value="">Selecione uma opção</option>
                {Object.keys(deliveryStatusMap).map((key, index) => (
                  <option key={'map_' + index} value={key}>
                    {deliveryStatusMap[key]}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row className="text-center mt-2">
            <Col
              style={{
                fontSize: 20,
                fontWeight: 'bold',
                color: '#053C5E',
              }}
            >
              <Button
                className={styles.clearFiltersBtn}
                onClick={() => {
                  setFilteredStatus('')
                  setFilterModal(false)
                }}
              >
                Limpar filtros
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </DashboardLayout>
  )
}

export default Entregas
